import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trash2, Ban, X, AlertTriangle } from 'lucide-react';

export interface ConfirmActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
  actionType: 'delete' | 'revoke';
  targetName: string;
  title?: string;
  description?: string;
}

export const ConfirmActionModal: React.FC<ConfirmActionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  actionType,
  targetName,
  title,
  description,
}) => {
  const [typedName, setTypedName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTypedName('');
      setLoading(false);
    }
  }, [isOpen]);

  const normalizedExpected = targetName.trim();
  const isMatch = typedName.trim() === normalizedExpected;

  const handleAction = async () => {
    if (!isMatch || loading) return;
    try {
      setLoading(true);
      await onConfirm();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const isDelete = actionType === 'delete';

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-[1100] p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.94, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.94, opacity: 0, y: 10 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="bg-[#FFFDF9] border border-[#E4D8C8] rounded-[22px] shadow-2xl max-w-md w-full p-6 sm:p-7 relative overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top Accent Strip */}
            <div
              className={`absolute top-0 left-0 right-0 h-1.5 ${
                isDelete ? 'bg-[#D64545]' : 'bg-[#D97706]'
              }`}
            />

            {/* Close Button */}
            <button
              onClick={onClose}
              disabled={loading}
              className="absolute top-4 right-4 text-[#8D8A83] hover:text-[#243238] p-1.5 rounded-xl hover:bg-[#EFE5D6] transition-colors cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Icon & Title */}
            <div className="flex items-start gap-4 mb-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${
                  isDelete
                    ? 'bg-rose-50 text-[#D64545] border border-rose-200'
                    : 'bg-amber-50 text-[#D97706] border border-amber-200'
                }`}
              >
                {isDelete ? (
                  <Trash2 className="w-6 h-6" />
                ) : (
                  <Ban className="w-6 h-6" />
                )}
              </div>
              <div className="pt-0.5 pr-6">
                <h3 className="text-base font-extrabold text-[#243238]">
                  {title || (isDelete ? 'Delete Invitation' : 'Revoke Invitation')}
                </h3>
                <p className="text-xs font-medium text-[#6F706C] mt-1 leading-relaxed">
                  {description ||
                    (isDelete
                      ? 'This will permanently remove this invitation record from the database. This action cannot be reversed.'
                      : 'This will revoke and deactivate the invitation link. The recipient will no longer be able to accept or onboard.')}
                </p>
              </div>
            </div>

            {/* Warning Box */}
            <div className="p-3.5 rounded-xl bg-[#F7F2EA] border border-[#DDD0BE] mb-5 flex items-center gap-2.5 text-xs text-[#243238]">
              <AlertTriangle className="w-4 h-4 text-[#8A4B22] shrink-0" />
              <span>
                To confirm, please retype the invitation name{' '}
                <strong className="font-extrabold text-[#1F5A43] select-all">
                  "{normalizedExpected}"
                </strong>{' '}
                below:
              </span>
            </div>

            {/* Input field */}
            <div className="space-y-1.5 mb-6">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-[#8D8A83]">
                Retype Name
              </label>
              <input
                type="text"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && isMatch) {
                    handleAction();
                  }
                }}
                placeholder={normalizedExpected}
                className="w-full px-3.5 py-2.5 text-xs font-semibold rounded-xl border border-[#DDD0BE] bg-white text-[#243238] placeholder:text-[#BBB] focus:border-[#1F5A43] focus:ring-2 focus:ring-[#1F5A43]/10 outline-none transition-all"
                autoFocus
              />
              {!isMatch && typedName.length > 0 && (
                <p className="text-[10px] font-semibold text-rose-600">
                  Name does not match yet
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-2 border-t border-[#E4D8C8]">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 text-xs font-bold text-[#6F706C] hover:bg-[#EFE5D6] rounded-xl transition-all cursor-pointer"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleAction}
                disabled={!isMatch || loading}
                className={`px-5 py-2 text-xs font-bold text-white rounded-xl transition-all flex items-center gap-2 shadow-xs cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed ${
                  isDelete
                    ? 'bg-[#D64545] hover:bg-rose-700'
                    : 'bg-[#D97706] hover:bg-amber-700'
                }`}
              >
                {loading && (
                  <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                )}
                <span>
                  {isDelete
                    ? loading
                      ? 'Deleting...'
                      : 'Delete Permanently'
                    : loading
                    ? 'Revoking...'
                    : 'Revoke Invitation'}
                </span>
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
